import React from 'react';

import { c } from 'ttag';

import { Badge, Button } from '@freelancelabs/components';

import { ContactsByEmailQuery } from '../../../graphql/__generated__/types';
import { EMPTY, formatDateTime } from '../display';
import { contactFullName, contactStatusReading } from './contactLookup';

export type MatchedContact = NonNullable<ContactsByEmailQuery['contactsByEmail']>['contacts'][number];

interface Props {
    contact: MatchedContact;
    selected: boolean;
    amongSeveral: boolean;
    onPick: () => void;
    onUnpick: () => void;
}

/**
 * One resolved contact: the identity as the source writes it, and the button that adopts it. It is a
 * candidate, not a result. A non-onboardable one stays on screen with its reason, button disabled.
 */
const ContactMatchCard = ({ contact, selected, amongSeveral, onPick, onUnpick }: Props) => {
    const reading = contactStatusReading(contact.onboardingStatus);
    const name = contactFullName(contact);

    // The source's accounts for this contact, truncated to the operator's perimeter. Empty is a real
    // answer, and these are where the contact sits at the source, not what the invitation will grant.
    const accounts = contact.accounts;

    return (
        <li
            className={
                selected
                    ? 'rounded-lg border border-admin-accent-border bg-admin-accent-soft p-4'
                    : 'rounded-lg border border-border-norm p-4'
            }
        >
            <div className="flex flex-wrap items-start gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-text-900">
                        {name ?? c('Onboarding contact').t`Contact without a name`}
                    </h3>
                    {/* The address the source writes, not the one typed: the match is case-insensitive. */}
                    <p className="font-mono text-xs text-text-600">{contact.email ?? EMPTY}</p>
                </div>
                <span className="ml-auto flex flex-wrap items-center gap-2">
                    <Badge category={reading.tone} size="small">
                        {reading.tag}
                    </Badge>
                    {selected && (
                        <Badge category="success" size="small">
                            {c('Onboarding contact').t`Picked`}
                        </Badge>
                    )}
                </span>
            </div>

            <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <div>
                    <dt className="text-text-600">{c('Onboarding contact field').t`Function`}</dt>
                    {/* Verbatim: normalising real job titles on screen would invent titles nobody carries. */}
                    <dd className="text-text-900">{contact.title ?? EMPTY}</dd>
                </div>
                <div>
                    <dt className="text-text-600">{c('Onboarding contact field').t`Phone`}</dt>
                    <dd className="font-mono text-text-900">{contact.phone ?? EMPTY}</dd>
                </div>
                <div>
                    <dt className="text-text-600">{c('Onboarding contact field').t`Source reference`}</dt>
                    <dd className="font-mono text-text-900">{contact.businessReference ?? EMPTY}</dd>
                </div>
                <div>
                    {/* A replicated record with no refresh date reads as up to date: a rule of the ingestion. */}
                    <dt className="text-text-600">{c('Onboarding contact field').t`Source last updated`}</dt>
                    <dd className="text-text-900">{formatDateTime(contact.sourceLastModifiedAt)}</dd>
                </div>
                <div className="sm:col-span-2">
                    <dt className="text-text-600">{c('Onboarding contact field').t`Companies at the source`}</dt>
                    <dd className="text-text-900">
                        {accounts.length === 0 ? (
                            <span className="text-text-600">
                                {c('Onboarding contact')
                                    .t`None within your portfolio — which is why this contact cannot be onboarded.`}
                            </span>
                        ) : (
                            <ul className="flex flex-col gap-0.5">
                                {accounts.map((account) => (
                                    <li key={account.id} className="flex flex-wrap items-center gap-2">
                                        <span>{account.name}</span>
                                        {/* Display only: the primary account drives no right. */}
                                        {account.isPrimary && (
                                            <Badge category="default" size="small">
                                                {c('Onboarding contact').t`Main company`}
                                            </Badge>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </dd>
                </div>
            </dl>

            {/* What the status means for the journey: always said, since "already onboarded"
                changes what the journey does without stopping it. */}
            {reading.meaning && (
                <p
                    className={reading.selectable ? 'mt-3 text-sm text-text-600' : 'mt-3 text-sm text-danger-600'}
                    role={reading.selectable ? 'status' : 'alert'}
                >
                    {reading.meaning}
                </p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-3">
                {selected ? (
                    <Button size="small" category="weak" onClick={onUnpick}>
                        {c('Onboarding action').t`This is not the right contact`}
                    </Button>
                ) : (
                    <Button size="small" category="default" disabled={!reading.selectable} onClick={onPick}>
                        {/* Two labels: with one candidate it confirms an identity, with several it settles which. */}
                        {amongSeveral
                            ? c('Onboarding action').t`Onboard this one`
                            : c('Onboarding action').t`This is the right contact`}
                    </Button>
                )}
                {/* The short qualifier next to the button, so the disabled control is never mute. */}
                {!reading.selectable && !selected && <span className="text-sm text-text-600">{reading.tag}</span>}
            </div>
        </li>
    );
};

export default ContactMatchCard;
